import {match} from 'ts-pattern';

import {
  TemplateContextEnum,
  useRenderBusinessCaseEmailTemplateExampleMutation,
  useRenderBusinessCaseSmsTemplateExampleMutation,
  useRenderCustomerEmailTemplateExampleMutation,
  useRenderCustomerSmsTemplateExampleMutation,
  useRenderRentalOrderEmailTemplateExampleMutation,
  useRenderRentalOrderSmsTemplateExampleMutation,
  useRenderServiceOrderEmailTemplateExampleMutation,
  useRenderServiceOrderSmsTemplateExampleMutation,
  useRenderVehicleEmailTemplateExampleMutation,
  useRenderVehicleSmsTemplateExampleMutation,
} from '@dms/api/core';

export const useRenderTemplateExample = (context: TemplateContextEnum | undefined) => {
  const [
    renderCustomerEmailTemplate,
    {
      isLoading: isRenderCustomerEmailInProgress,
      data: renderDataCustomerEmail,
      isError: isRenderCustomerEmailError,
    },
  ] = useRenderCustomerEmailTemplateExampleMutation();
  const [
    renderCustomerSmsTemplate,
    {
      isLoading: isRenderCustomerSmsInProgress,
      data: renderDataCustomerSms,
      isError: isRenderCustomerSmsError,
    },
  ] = useRenderCustomerSmsTemplateExampleMutation();

  const [
    renderBusinessCaseEmailTemplate,
    {
      isLoading: isRenderBusinessCaseEmailInProgress,
      data: renderDataBusinessCaseEmail,
      isError: isRenderBusinessCaseEmailError,
    },
  ] = useRenderBusinessCaseEmailTemplateExampleMutation();
  const [
    renderBusinessCaseSmsTemplate,
    {
      isLoading: isRenderBusinessCaseSmsInProgress,
      data: renderDataBusinessCaseSms,
      isError: isRenderBusinessCaseSmsError,
    },
  ] = useRenderBusinessCaseSmsTemplateExampleMutation();

  const [
    renderServiceOrderSmsTemplate,
    {
      isLoading: isRenderServiceOrderSmsInProgress,
      data: renderDataServiceOrderSms,
      isError: isRenderServiceOrderSmsError,
    },
  ] = useRenderServiceOrderSmsTemplateExampleMutation();
  const [
    renderServiceOrderEmailTemplate,
    {
      isLoading: isRenderServiceOrderEmailInProgress,
      data: renderDataServiceOrderEmail,
      isError: isRenderServiceOrderEmailError,
    },
  ] = useRenderServiceOrderEmailTemplateExampleMutation();

  const [
    renderRentalOrderEmailTemplate,
    {
      isLoading: isRenderRentalOrderEmailInProgress,
      data: renderDataRentalOrderEmail,
      isError: isRenderRentalOrderEmailError,
    },
  ] = useRenderRentalOrderEmailTemplateExampleMutation();
  const [
    renderRentalOrderSmsTemplate,
    {
      isLoading: isRenderRentalOrderSmsInProgress,
      data: renderDataRentalOrderSms,
      isError: isRenderRentalOrderSmsError,
    },
  ] = useRenderRentalOrderSmsTemplateExampleMutation();

  const [
    renderVehicleEmailTemplate,
    {
      isLoading: isRenderVehicleEmailInProgress,
      data: renderDataVehicleEmail,
      isError: isRenderVehicleEmailError,
    },
  ] = useRenderVehicleEmailTemplateExampleMutation();
  const [
    renderVehicleSmsTemplate,
    {
      isLoading: isRenderVehicleSmsInProgress,
      data: renderDataVehicleSms,
      isError: isRenderVehicleSmsError,
    },
  ] = useRenderVehicleSmsTemplateExampleMutation();

  const [renderEmail, emailData] = match<
    TemplateContextEnum | undefined,
    [
      (
        | typeof renderCustomerEmailTemplate
        | typeof renderServiceOrderEmailTemplate
        | typeof renderBusinessCaseEmailTemplate
        | typeof renderRentalOrderEmailTemplate
        | typeof renderVehicleEmailTemplate
        | undefined
      ),
      (
        | typeof renderDataCustomerEmail
        | typeof renderDataServiceOrderEmail
        | typeof renderDataBusinessCaseEmail
        | typeof renderDataRentalOrderEmail
        | typeof renderDataVehicleEmail
        | undefined
      ),
    ]
  >(context)
    .with('customer', () => [renderCustomerEmailTemplate, renderDataCustomerEmail])
    .with('service-order', () => [renderServiceOrderEmailTemplate, renderDataServiceOrderEmail])
    .with('business-case', () => [renderBusinessCaseEmailTemplate, renderDataBusinessCaseEmail])
    .with('rental-order', () => [renderRentalOrderEmailTemplate, renderDataRentalOrderEmail])
    .with('vehicle', () => [renderVehicleEmailTemplate, renderDataVehicleEmail])
    .otherwise(() => [undefined, undefined]);

  const [renderSms, smsData] = match<
    TemplateContextEnum | undefined,
    [
      (
        | typeof renderCustomerSmsTemplate
        | typeof renderServiceOrderSmsTemplate
        | typeof renderBusinessCaseSmsTemplate
        | typeof renderRentalOrderSmsTemplate
        | typeof renderVehicleSmsTemplate
        | undefined
      ),
      (
        | typeof renderDataCustomerSms
        | typeof renderDataServiceOrderSms
        | typeof renderDataBusinessCaseSms
        | typeof renderDataRentalOrderSms
        | typeof renderDataVehicleSms
        | undefined
      ),
    ]
  >(context)
    .with('customer', () => [renderCustomerSmsTemplate, renderDataCustomerSms])
    .with('service-order', () => [renderServiceOrderSmsTemplate, renderDataServiceOrderSms])
    .with('business-case', () => [renderBusinessCaseSmsTemplate, renderDataBusinessCaseSms])
    .with('rental-order', () => [renderRentalOrderSmsTemplate, renderDataRentalOrderSms])
    .with('vehicle', () => [renderVehicleSmsTemplate, renderDataVehicleSms])
    .otherwise(() => [undefined, undefined]);

  const isLoading =
    isRenderCustomerEmailInProgress ||
    isRenderCustomerSmsInProgress ||
    isRenderBusinessCaseEmailInProgress ||
    isRenderBusinessCaseSmsInProgress ||
    isRenderServiceOrderEmailInProgress ||
    isRenderServiceOrderSmsInProgress ||
    isRenderRentalOrderEmailInProgress ||
    isRenderRentalOrderSmsInProgress ||
    isRenderVehicleEmailInProgress ||
    isRenderVehicleSmsInProgress;

  const isError =
    isRenderCustomerEmailError ||
    isRenderCustomerSmsError ||
    isRenderBusinessCaseEmailError ||
    isRenderBusinessCaseSmsError ||
    isRenderServiceOrderEmailError ||
    isRenderServiceOrderSmsError ||
    isRenderRentalOrderEmailError ||
    isRenderRentalOrderSmsError ||
    isRenderVehicleEmailError ||
    isRenderVehicleSmsError;

  return {
    renderSms,
    renderEmail,
    smsData,
    emailData,
    isLoading,
    isError,
  };
};
